/**
 * Visual Agent Architect Types
 *
 * Types for the drag-and-drop workflow builder
 */
import { Node, Edge } from '@xyflow/react'

// ==================== Node Types ====================

export type AgentNodeType =
  | 'specialist'      // Ziro specialist agent
  | 'supervisor'      // Supervisor/orchestrator node
  | 'router'          // Conditional routing node
  | 'aggregator'      // Result aggregation node
  | 'input'           // User input start node
  | 'output'          // Final output node

export interface AgentNodeData {
  label: string
  description?: string
  nodeType: AgentNodeType

  // For specialist nodes
  specialistType?: string
  agentId?: string

  // Provider configuration
  providerMode?: 'local' | 'api' | 'auto' | 'hybrid'
  localModel?: string
  apiProvider?: string
  apiModel?: string

  // Execution configuration
  executionMode?: 'direct' | 'simple' | 'triad' | 'full'
  enableQualityReview?: boolean
  qualityThreshold?: number
  maxRevisions?: number

  // Router specific
  conditions?: RouterCondition[]

  // Aggregator specific
  aggregationStrategy?: 'concatenate' | 'summarize' | 'vote' | 'best'

  // UI state
  isConfigured: boolean
  isExpanded?: boolean
}

export interface RouterCondition {
  id: string
  condition: string
  targetNodeId: string
  description?: string
}

export type AgentNode = Node<AgentNodeData>

// ==================== Edge Types ====================

export type AgentEdgeType =
  | 'default'         // Normal flow
  | 'conditional'     // Conditional branch
  | 'parallel'        // Parallel execution

export interface AgentEdgeData {
  edgeType: AgentEdgeType
  label?: string
  condition?: string
  priority?: number
}

export type AgentEdge = Edge<AgentEdgeData>

// ==================== Workflow Types ====================

export interface WorkflowConfig {
  id: string
  name: string
  description?: string
  nodes: AgentNode[]
  edges: AgentEdge[]

  // Global settings
  defaultProviderMode: 'local' | 'api' | 'auto' | 'hybrid'
  defaultExecutionMode: 'direct' | 'simple' | 'triad' | 'full'
  parallelExecution: boolean
  maxConcurrency: number

  // Metadata
  createdAt: string
  updatedAt: string
  version: number
  isPublished: boolean
}

// ==================== Palette Items ====================

export interface PaletteItem {
  type: AgentNodeType
  label: string
  description: string
  icon: string
  color: string
  defaultData: Partial<AgentNodeData>
}

export const PALETTE_ITEMS: PaletteItem[] = [
  {
    type: 'input',
    label: 'Input',
    description: 'User input entry point',
    icon: 'ArrowRightStartOnRectangleIcon',
    color: 'green',
    defaultData: { label: 'Input', nodeType: 'input', isConfigured: true }
  },
  {
    type: 'specialist',
    label: 'Specialist',
    description: 'Ziro Intelligence specialist agent',
    icon: 'SparklesIcon',
    color: 'primary',
    defaultData: { label: 'Specialist', nodeType: 'specialist', isConfigured: false }
  },
  {
    type: 'supervisor',
    label: 'Supervisor',
    description: 'Orchestrates and coordinates agents',
    icon: 'UserGroupIcon',
    color: 'purple',
    defaultData: { label: 'Supervisor', nodeType: 'supervisor', isConfigured: false }
  },
  {
    type: 'router',
    label: 'Router',
    description: 'Conditional routing based on conditions',
    icon: 'ArrowsPointingOutIcon',
    color: 'yellow',
    defaultData: { label: 'Router', nodeType: 'router', isConfigured: false, conditions: [] }
  },
  {
    type: 'aggregator',
    label: 'Aggregator',
    description: 'Combines results from multiple agents',
    icon: 'Square3Stack3DIcon',
    color: 'blue',
    defaultData: { label: 'Aggregator', nodeType: 'aggregator', isConfigured: false, aggregationStrategy: 'concatenate' }
  },
  {
    type: 'output',
    label: 'Output',
    description: 'Final output destination',
    icon: 'ArrowRightEndOnRectangleIcon',
    color: 'red',
    defaultData: { label: 'Output', nodeType: 'output', isConfigured: true }
  }
]

// ==================== Specialist Types ====================

export const SPECIALIST_TYPES = [
  { value: 'coding', label: 'Coding', icon: 'CodeBracketIcon' },
  { value: 'documents', label: 'Documents', icon: 'DocumentTextIcon' },
  { value: 'graphs', label: 'Graphs & Charts', icon: 'ChartBarIcon' },
  { value: 'spreadsheets', label: 'Spreadsheets', icon: 'TableCellsIcon' },
  { value: 'presentations', label: 'Presentations', icon: 'PresentationChartBarIcon' },
  { value: 'data_analysis', label: 'Data Analysis', icon: 'BeakerIcon' },
  { value: 'literature_review', label: 'Literature Review', icon: 'BookOpenIcon' },
  { value: 'general', label: 'General', icon: 'CpuChipIcon' },
] as const

// ==================== Preset Templates ====================

export interface WorkflowTemplate {
  id: string
  name: string
  description: string
  category: 'simple' | 'advanced' | 'custom'
  nodes: Partial<AgentNode>[]
  edges: Partial<AgentEdge>[]
}

export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'simple-chat',
    name: 'Simple Chat',
    description: 'Direct input to single specialist output',
    category: 'simple',
    nodes: [
      { id: 'input', position: { x: 100, y: 200 }, data: { label: 'Input', nodeType: 'input', isConfigured: true } },
      { id: 'specialist', position: { x: 350, y: 200 }, data: { label: 'Specialist', nodeType: 'specialist', isConfigured: false } },
      { id: 'output', position: { x: 600, y: 200 }, data: { label: 'Output', nodeType: 'output', isConfigured: true } },
    ],
    edges: [
      { id: 'e1', source: 'input', target: 'specialist' },
      { id: 'e2', source: 'specialist', target: 'output' },
    ]
  },
  {
    id: 'supervised-triad',
    name: 'Supervised Triad',
    description: 'Supervisor orchestrating multiple specialists with quality review',
    category: 'advanced',
    nodes: [
      { id: 'input', position: { x: 100, y: 250 }, data: { label: 'Input', nodeType: 'input', isConfigured: true } },
      { id: 'supervisor', position: { x: 350, y: 250 }, data: { label: 'Supervisor', nodeType: 'supervisor', isConfigured: false } },
      { id: 'specialist1', position: { x: 600, y: 100 }, data: { label: 'Specialist 1', nodeType: 'specialist', isConfigured: false } },
      { id: 'specialist2', position: { x: 600, y: 250 }, data: { label: 'Specialist 2', nodeType: 'specialist', isConfigured: false } },
      { id: 'specialist3', position: { x: 600, y: 400 }, data: { label: 'Specialist 3', nodeType: 'specialist', isConfigured: false } },
      { id: 'aggregator', position: { x: 850, y: 250 }, data: { label: 'Aggregator', nodeType: 'aggregator', isConfigured: false, aggregationStrategy: 'summarize' } },
      { id: 'output', position: { x: 1100, y: 250 }, data: { label: 'Output', nodeType: 'output', isConfigured: true } },
    ],
    edges: [
      { id: 'e1', source: 'input', target: 'supervisor' },
      { id: 'e2', source: 'supervisor', target: 'specialist1' },
      { id: 'e3', source: 'supervisor', target: 'specialist2' },
      { id: 'e4', source: 'supervisor', target: 'specialist3' },
      { id: 'e5', source: 'specialist1', target: 'aggregator' },
      { id: 'e6', source: 'specialist2', target: 'aggregator' },
      { id: 'e7', source: 'specialist3', target: 'aggregator' },
      { id: 'e8', source: 'aggregator', target: 'output' },
    ]
  },
  {
    id: 'conditional-router',
    name: 'Conditional Router',
    description: 'Routes to different specialists based on input analysis',
    category: 'advanced',
    nodes: [
      { id: 'input', position: { x: 100, y: 250 }, data: { label: 'Input', nodeType: 'input', isConfigured: true } },
      { id: 'router', position: { x: 350, y: 250 }, data: { label: 'Router', nodeType: 'router', isConfigured: false } },
      { id: 'coding', position: { x: 600, y: 100 }, data: { label: 'Coding', nodeType: 'specialist', specialistType: 'coding', isConfigured: true } },
      { id: 'documents', position: { x: 600, y: 250 }, data: { label: 'Documents', nodeType: 'specialist', specialistType: 'documents', isConfigured: true } },
      { id: 'data', position: { x: 600, y: 400 }, data: { label: 'Data Analysis', nodeType: 'specialist', specialistType: 'data_analysis', isConfigured: true } },
      { id: 'output', position: { x: 850, y: 250 }, data: { label: 'Output', nodeType: 'output', isConfigured: true } },
    ],
    edges: [
      { id: 'e1', source: 'input', target: 'router' },
      { id: 'e2', source: 'router', target: 'coding', data: { edgeType: 'conditional', label: 'Code tasks' } },
      { id: 'e3', source: 'router', target: 'documents', data: { edgeType: 'conditional', label: 'Doc tasks' } },
      { id: 'e4', source: 'router', target: 'data', data: { edgeType: 'conditional', label: 'Data tasks' } },
      { id: 'e5', source: 'coding', target: 'output' },
      { id: 'e6', source: 'documents', target: 'output' },
      { id: 'e7', source: 'data', target: 'output' },
    ]
  }
]
